// src/components/quiz/LoadingScreen.tsx
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const STEPS = [
  'Analysing your responses…',
  'Mapping your hormonal profile…',
  'Generating your insights…',
  'Preparing your results…',
]

interface LoadingScreenProps {
  onComplete: () => void
}

export function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const [step, setStep] = useState(0)
  const [lineWidth, setLineWidth] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setStep(s => {
        if (s < STEPS.length - 1) return s + 1
        clearInterval(interval)
        return s
      })
      setLineWidth(w => Math.min(w + 25, 100))
    }, 900)

    // Complete after all steps + a brief pause
    const timer = setTimeout(onComplete, STEPS.length * 900 + 600)

    return () => {
      clearInterval(interval)
      clearTimeout(timer)
    }
  }, [onComplete])

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--color-deep-teal)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
    }}>
      <div style={{ width: '100%', maxWidth: '400px', textAlign: 'center' }}>
        {/* Animated text */}
        <div style={{ height: '2rem', marginBottom: '2rem', overflow: 'hidden' }}>
          <AnimatePresence mode="wait">
            <motion.p
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: '1.125rem',
                color: 'rgba(255,255,255,0.9)',
              }}
            >
              {STEPS[step]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Progress line */}
        <div style={{
          width: '100%',
          height: '2px',
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          <motion.div
            animate={{ width: `${lineWidth}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            style={{
              height: '100%',
              background: 'var(--color-teal)',
              borderRadius: '2px',
            }}
          />
        </div>

        <p style={{
          marginTop: '1rem',
          fontSize: '0.75rem',
          color: 'rgba(255,255,255,0.4)',
        }}>
          Just a moment…
        </p>
      </div>
    </div>
  )
}
